import { useMemo, useRef, useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import 'antd/dist/antd.min.css';

import TranslateIntlProvider from 'translations/TranslateIntlProvider';
import MessageContextProvider from 'utils/context/MessagesContext';
import BrandContextProvider, { useBrand } from 'utils/context/BrandContext';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import BugsnagWrapper from './components/Bugsnag/BugsnagWrapper';
import HotjarProvider from './components/HotjarProvider';
import MaintenanceMode from './components/MaintenanceMode';
import { createApolloClient } from './graphql/client';
import LoginPage from './pages/LoginPage';
import Fallback from './router/Fallback';
import MainRouter from './router/MainRouter';
import './styles/index.scss';

// Component that creates Apollo client with Auth context
const ApolloClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accessToken } = useAuth();
  const { brand } = useBrand();
  const tokenRef = useRef(accessToken);

  // Keep the ref updated
  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  // Recreate Apollo client when brand changes to ensure fresh cache
  const apolloClient = useMemo(() => {
    const getAuthToken = () => {
      return tokenRef.current || undefined;
    };

    return createApolloClient(getAuthToken);
  }, [brand]); // Recreate when brand changes

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const AuthenticatedApp: React.FC = () => {
  const maintenanceModeIsOn = false;
  const productionDomain = 'app.memoweb.be';

  return (
    <HotjarProvider>
      {maintenanceModeIsOn && window.location.hostname.startsWith(productionDomain) ? (
        <MaintenanceMode />
      ) : (
        <MessageContextProvider>
          <MainRouter />
        </MessageContextProvider>
      )}
    </HotjarProvider>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, state, user } = useAuth();
  const { brand, brandConfig } = useBrand();

  // Apply brand class for styling
  useEffect(() => {
    const appName = brandConfig.name === 'memo' ? 'MemoWeb' : 'OffiWeb';

    document.body.className = document.body.className.replace(/brand-\w+/g, '').trim();
    document.body.classList.add(`brand-${brand}`);
    document.documentElement.classList.add(`brand-${brand}`);

    if (document.title === 'MemoWeb' || !document.title) {
      document.title = appName;
    }
  }, [brand, brandConfig]);

  // Show loading while checking session
  if (state.isLoading) {
    return <Fallback />;
  }

  // If authenticated, always show the authenticated app (don't check routes)
  if (isAuthenticated) {
    return (
      <ApolloClientProvider>
        <TranslateIntlProvider userLocale={user?.locale}>
          <AuthenticatedApp />
        </TranslateIntlProvider>
      </ApolloClientProvider>
    );
  }

  // For unauthenticated users, check if current route is public
  // Use window.location since Router context doesn't exist yet
  const currentPath = window.location.pathname;
  const isMagicLinkRoute = currentPath === '/authentication/magic';
  const isPublicRoute = currentPath === '/privacy-policy' || isMagicLinkRoute;

  // Show MainRouter for public routes, LoginPage for others
  if (isPublicRoute) {
    // Allow access to public routes (magic link, privacy policy)
    // Wrap in ApolloClientProvider since MainRouter contains WebpushManager which uses Apollo hooks
    return (
      <ApolloClientProvider>
        <TranslateIntlProvider>
          <MainRouter />
        </TranslateIntlProvider>
      </ApolloClientProvider>
    );
  }

  // Show login page for other routes
  return (
    <TranslateIntlProvider>
      <LoginPage />
    </TranslateIntlProvider>
  );
};

const App: React.FC = () => {
  return (
    <BugsnagWrapper>
      <AuthProvider LoadingComponent={<Fallback />}>
        <BrandContextProvider>
          <AppContent />
        </BrandContextProvider>
      </AuthProvider>
    </BugsnagWrapper>
  );
};

export default App;
